import { Fragment, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { KEYWORD_LINKS } from "./article-index";

/**
 * Escape a string for safe use inside a RegExp.
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build a single case-insensitive regex from the keyword map.
 * Longest keys first so multi-word phrases win over sub-phrases.
 * `\b` word boundaries prevent partial-word matches inside longer words.
 */
function buildMatcher(currentHref: string): {
  regex: RegExp;
  map: Map<string, string>;
} {
  const map = new Map<string, string>();
  for (const [k, href] of Object.entries(KEYWORD_LINKS)) {
    if (href === currentHref) continue; // never self-link
    map.set(k.toLowerCase(), href);
  }
  const keys = Array.from(map.keys()).sort((a, b) => b.length - a.length);
  if (keys.length === 0) {
    return { regex: /$^/, map };
  }
  const regex = new RegExp(`\\b(${keys.map(escapeRegExp).join("|")})\\b`, "gi");
  return { regex, map };
}

/**
 * autoLinkText — scans a paragraph string and replaces the FIRST occurrence
 * of each configured keyword with an in-app <Link>. Later occurrences of the
 * same keyword in the same paragraph are left as plain text to avoid noisy
 * over-linking. Skips self-links to the current article's href.
 */
export function autoLinkText(text: string, currentHref: string): ReactNode {
  if (!text) return text;
  const { regex, map } = buildMatcher(currentHref);
  if (!map.size) return text;

  const out: ReactNode[] = [];
  const used = new Set<string>();
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = regex.exec(text)) !== null) {
    const matched = m[0];
    const key = matched.toLowerCase();
    const href = map.get(key);
    if (!href || used.has(key)) continue;
    used.add(key);
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <Link title={`Read the LifeWestWay guide on ${matched}`}
        key={`${key}-${m.index}`}
        to={href}
        className="text-foreground underline decoration-accent/60 decoration-2 underline-offset-2 hover:text-accent"
      >
        {matched}
      </Link>,
    );
    last = m.index + matched.length;
  }
  if (last < text.length) out.push(text.slice(last));
  if (out.length === 0) return text;
  return (
    <>
      {out.map((n, i) => (
        <Fragment key={i}>{n}</Fragment>
      ))}
    </>
  );
}
